import React from "react";
import { X, Info, Hash, Tag, User, UserMinus, Clock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/ui/components/ui/dialog";
import { Button } from "@/ui/components/ui/button";
import { Card, CardContent } from "@/ui/components/ui/card";
import { ListContainer } from "@/ui/components/shared/ListItem";
import { useAppState } from "@/state/legacy_module_api";
import { Message } from "@/data/schemas";
import { formatTimestamp } from "@/core_bridge/module_api";

interface MessageBubbleInfoPopupProps {
  opened: boolean;
  onPopupClose: () => void;
  message?: Message | null;
}

const MessageBubbleInfoPopup: React.FC<MessageBubbleInfoPopupProps> = ({
  opened,
  onPopupClose,
  message,
}) => {
  const { contactsById } = useAppState().user;
  const myUserId = useAppState().myUserId;

  if (!message) return null;

  const senderName = contactsById[message.senderId]?.username || "Unknown User";
  const receiverName =
    contactsById[message.receiverId || ""]?.username || "Unknown User";
  const isMyMessage = message.senderId === myUserId;

  // Use switch-core formatTimestamp utility
  const formatDate = (timestamp: number) => timestamp ? formatTimestamp(timestamp) : "Unknown";

  const getMessageTypeDisplay = (type: string) => {
    switch (type) {
      case "MESSAGE":
        return "Text Message";
      case "CALL":
        return "Call Message";
      case "CALL_JOINED":
        return "Call Join";
      case "COMMUNITY_UPDATED":
        return "Community Update";
      case "COMMUNITY_THREAD_JOIN_REQUEST":
        return "Group Join Request";
      case "COMMUNITY_THREAD_UPDATED":
        return "Community Group Update";
      case "THREAD_CREATED":
        return "Group Created";
      case "EXIT_THREAD":
        return "Exit Group";
      default:
        return type || "Unknown";
    }
  };

  return (
    <Dialog open={opened} onOpenChange={(open) => !open && onPopupClose()}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Message Info</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <Info className="w-12 h-12 text-primary mx-auto mb-2" />
              <h3 className="font-semibold text-lg">
                {getMessageTypeDisplay(message.type)}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isMyMessage ? "Sent by you" : `Received from ${senderName}`}
              </p>
            </CardContent>
          </Card>

          <ListContainer>
            <div className="flex items-center gap-3 px-4 py-3 border-b">
              <Hash className="w-5 h-5 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Message ID</p>
                <p className="font-medium truncate">{message.id || "Unknown"}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-4 py-3 border-b">
              <Tag className="w-5 h-5 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Type</p>
                <p className="font-medium">{getMessageTypeDisplay(message.type)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-4 py-3 border-b">
              <User className="w-5 h-5 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Sender</p>
                <p className="font-medium">{senderName}</p>
              </div>
            </div>

            {message.receiverId && (
              <div className="flex items-center gap-3 px-4 py-3 border-b">
                <UserMinus className="w-5 h-5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Receiver</p>
                  <p className="font-medium">{receiverName}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 px-4 py-3">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="font-medium">{formatDate(message.createdAt)}</p>
              </div>
            </div>
          </ListContainer>

          {message.content && (
            <Card>
              <CardContent className="pt-6">
                <h4 className="font-medium mb-3">Message Content</h4>
                <div className="p-3 bg-muted rounded-lg text-sm leading-relaxed whitespace-pre-wrap break-words max-h-48 overflow-auto">
                  {typeof message.content === "string"
                    ? message.content
                    : JSON.stringify(message.content, null, 2)}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MessageBubbleInfoPopup;
